import React from 'react'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch, Action } from 'redux'
import { IApplicationState } from '@Site/rootReducer'
import { Link } from 'react-router-dom'
import classnames from 'classnames'
import formattedCreditsWithHoursShort from '@Utils/FormattedCreditsWithHoursShort'
import Button from '@SiteComponents/Button'
import LogoIcon from '@Images/logo.svg'
import Logout from '@Images/account_popup_logout.svg'

import {
  ICreateCustomerPortalSessionPayload,
  ILogoutPayload,
} from '@SiteModules/User/types'

import {
  logout,
  createCustomerPortalSession,
} from '@SiteModules/User/actions'

import {
  getCurrentAccount,
} from '@SiteModules/Account/selectors'

import './Header.scss'

interface HeaderProps {
  readonly subscriptionIsCurrent: boolean
  readonly availableTotalCredits: number
  readonly hasLoadedSuccessfully: boolean
  readonly cancelAtPeriodEnd: boolean
  readonly renewalDate: string
  readonly shouldNotLinkToDashboard?: boolean
  readonly hideCredits?: boolean
  readonly hideSalesLink?: boolean
  readonly currentAccountId: number
  createCustomerPortalSession(payload: ICreateCustomerPortalSessionPayload): void
  logout(payload: ILogoutPayload): {}
}

interface HeaderState {
  readonly showInfoForCredits: boolean
  readonly infoForCreditsTimeout: ReturnType<typeof setTimeout> | null
}

class Header extends React.Component<HeaderProps, HeaderState> {
  constructor(props: HeaderProps) {
    super(props)

    this.state = {
      showInfoForCredits: false,
      infoForCreditsTimeout: null,
    }
  }

  toggleShowInfoForCredits = () => {
    if (this.state.infoForCreditsTimeout) {
      this.setState({ showInfoForCredits: false })
      clearTimeout(this.state.infoForCreditsTimeout)
      this.setState({ infoForCreditsTimeout: null })
    } else {
      const infoForCreditsTimeout = setTimeout(() => {
        this.setState({ showInfoForCredits: true })
      }, window.Checksub.tooltipDelay)
      this.setState({ infoForCreditsTimeout })
    }
  }

  createCustomerPortalSession = () => {
    this.props.createCustomerPortalSession({ accountId: this.props.currentAccountId })
  }

  handleLogout = () => {
    this.props.logout({ forwardingUrl: '/users/sign_in' })
  }

  render() {
    const {
      subscriptionIsCurrent,
      availableTotalCredits,
      hasLoadedSuccessfully,
      cancelAtPeriodEnd,
      renewalDate,
      shouldNotLinkToDashboard,
      hideCredits,
      hideSalesLink,
    } = this.props

    const shouldRenderSubscriptionCredits = hasLoadedSuccessfully && subscriptionIsCurrent
    const shouldShowInfoForCredits = this.state.showInfoForCredits && !cancelAtPeriodEnd

    const renderSubscriptionCredits = () => {
      return (
        <div className={classnames(
          'Header__credits-wrapper',
          { 'Header__credits-wrapper-hidden': !shouldRenderSubscriptionCredits }
        )}>
          <div
            className="Header__credits"
            onMouseEnter={this.toggleShowInfoForCredits}
            onMouseLeave={this.toggleShowInfoForCredits}
          >
            Credits : {formattedCreditsWithHoursShort(availableTotalCredits)}
          </div>
          {shouldShowInfoForCredits && (
            <div className="Header__tooltip">
              Plan will be renewed on {renewalDate}
            </div>
          )}
        </div>
      )
    }

    return (
      <header className="Header">
        {shouldNotLinkToDashboard ? (
          <LogoIcon className="Header__logo" />
        ) : (
          <Link to="/">
            <LogoIcon className="Header__logo" />
          </Link>
        )}

        {!hideSalesLink && (
          <a
            href="https://calendly.com/meetflorian/enterprise-demo"
            className="Header__contact-sales"
            target="_blank"
          >
            Contact Sales
          </a>
        )}

        <div className="Header__right">
          {!hideCredits && renderSubscriptionCredits()}
          <div className="Header__logout">
            <Button
              styleType="gray-subtle"
              size="small"
              caption="Log out"
              icon={<Logout />}
              onClick={this.handleLogout}
            />
          </div>
        </div>
      </header>
    )
  }
}

function mapStateToProps(state: IApplicationState) {
  const { user, accounts } = state

  const currentAccount = getCurrentAccount(state)
  const currentAccountSubscription = currentAccount?.subscription

  return {
    subscriptionIsCurrent: !!currentAccountSubscription?.attributes.isCurrent,
    availableTotalCredits: currentAccount?.attributes.availableTotalCredits || 0,
    hasLoadedSuccessfully: user.hasLoadedSuccessfully,
    cancelAtPeriodEnd: !!currentAccountSubscription?.attributes.cancelAtPeriodEnd,
    renewalDate: currentAccountSubscription?.attributes.renewalDate || '',
    currentAccountId: accounts.currentAccountId,
  }
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({
    logout,
    createCustomerPortalSession,
  }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(Header)
